/**
 * BriefingDecisionTiles — the four «Braucht deinen Entscheid» entry tiles of the
 * Heute Briefing (Story UI-U10, AC2). Ports the prototype's tinted Entscheid-Kacheln
 * (heute-v2-view.tsx ENTRIES) to OUR primitives + design tokens.
 *
 * Color = signal (design.md §2 escalation family): each tile is tinted in its own
 * status family (Info → Warning → Danger), a documented soft-tint entry surface —
 * NOT a filled status pill. Counts (mono-num CountBadge) come from the feature-detected
 * PendingWorkSummary; a null summary → tiles WITHOUT counts (defensive). The Eingang
 * tile has no summary field, so it never carries a count.
 *
 * Navigation is a TanStack `Link` (no imperative navigate) so tiles are real links.
 *
 * [Source: brokly-design-reference/components/brokly/heute-v2-view.tsx ENTRIES/TILE_TONE]
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §2 §5]
 * [Source: docs/stories/ui-u10-brok-briefing-ray.md AC2]
 */
import { Link } from '@tanstack/react-router';
import { ArrowRight, ListTodo, MessageSquare, ReceiptText, RefreshCw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PendingWorkSummary } from '@brokly/shared';
import { CountBadge } from '../../../components/ui/CountBadge';
import { cn } from '../../../lib/cn';
import type { RoutePath } from '../../../app/shell/nav';
import { KOMMUNIKATION_ENABLED } from '../../../lib/feature-flags';

type Tone = 'info' | 'decide' | 'alert';

/** Tinted-but-not-filled entry surfaces + own-color icon square, per status family. */
const TILE_TONE: Record<Tone, string> = {
  info: 'border-status-info-border bg-status-info-bg/50 hover:bg-status-info-bg',
  decide: 'border-status-warning-border bg-status-warning-bg/50 hover:bg-status-warning-bg',
  alert: 'border-status-danger-border bg-status-danger-bg/50 hover:bg-status-danger-bg',
};
const TILE_ICON: Record<Tone, string> = {
  info: 'bg-card text-status-info',
  decide: 'bg-card text-status-warning',
  alert: 'bg-card text-status-danger',
};

interface TileDef {
  to: RoutePath;
  label: string;
  hint: string;
  icon: LucideIcon;
  tone: Tone;
  /** How to read this tile's count off the summary — or null (no count source). */
  read: ((s: PendingWorkSummary) => number) | null;
}

const TILES: TileDef[] = [
  {
    to: '/rechnungen',
    label: 'Rechnungen freigeben',
    hint: 'Stufe 3 bis 4, brauchen Entscheid',
    icon: ReceiptText,
    tone: 'decide',
    read: (s) => s.invoices.awaitingCount,
  },
  {
    to: KOMMUNIKATION_ENABLED ? '/kommunikation' : '/posteingang',
    label: 'Eingang triagieren',
    hint: 'Nachrichten zu sichten',
    icon: MessageSquare,
    tone: 'info',
    read: null,
  },
  {
    to: '/renewals',
    label: 'Renewals ≤ 14 Tage',
    hint: 'Fällige Erneuerungen',
    icon: RefreshCw,
    tone: 'decide',
    read: (s) => s.renewals.red,
  },
  {
    to: '/aufgaben',
    label: 'Kritische Aufgaben',
    hint: 'Hohe Priorität',
    icon: ListTodo,
    tone: 'alert',
    read: (s) => s.tasks.count,
  },
];

export function BriefingDecisionTiles({ summary }: { summary: PendingWorkSummary | null }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {TILES.map((t) => {
        const Icon = t.icon;
        const count = summary && t.read ? t.read(summary) : undefined;
        return (
          <Link
            key={t.to}
            to={t.to}
            className={cn(
              'group flex items-center gap-3 rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              TILE_TONE[t.tone],
            )}
          >
            <span
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-md',
                TILE_ICON[t.tone],
              )}
            >
              <Icon className="size-4" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-foreground">{t.label}</span>
              <span className="truncate text-xs text-muted-foreground">{t.hint}</span>
            </span>
            {count !== undefined && <CountBadge count={count} />}
            <ArrowRight className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
          </Link>
        );
      })}
    </div>
  );
}
